import { motion } from 'framer-motion';
import {
  BookOpen, Briefcase, Building, Building2,
  Dumbbell, FileDown, GraduationCap, HardHat, Heart, Home,
  Key, Landmark, Loader2, ShoppingBag, Ticket, Truck,
} from 'lucide-react';
import { useState } from 'react';
import SEOHead from '../components/SEOHead';

const SECTORS = [
  { id: 'hotel',      label: 'Hotels & Resorts',       Icon: Building2,     tagline: 'Guest check-in & registration' },
  { id: 'hospital',   label: 'Clinics & Hospitals',     Icon: Heart,         tagline: 'Patient onboarding & verification' },
  { id: 'corporate',  label: 'Corporate Offices',       Icon: Briefcase,     tagline: 'Visitor & employee check-in' },
  { id: 'events',     label: 'Events & Venues',         Icon: Ticket,        tagline: 'Attendee registration & entry' },
  { id: 'banking',    label: 'Banks & NBFCs',           Icon: Landmark,      tagline: 'Branch KYC & account opening' },
  { id: 'apartment',  label: 'Gated Communities',       Icon: Home,          tagline: 'Resident & visitor gate check-in' },
  { id: 'government', label: 'Govt / Jan Seva Kendra',  Icon: Building,      tagline: 'Citizen service centre access' },
  { id: 'gym',        label: 'Gym & Fitness',           Icon: Dumbbell,      tagline: 'Member onboarding & daily check-in' },
  { id: 'cowork',     label: 'Co-working Spaces',       Icon: Key,           tagline: 'Member & visitor access log' },
  { id: 'coaching',   label: 'Coaching Institutes',     Icon: BookOpen,      tagline: 'Student enrolment verification' },
  { id: 'education',  label: 'Schools & Colleges',      Icon: GraduationCap, tagline: 'Campus visitor & exam check-in' },
  { id: 'hostel',     label: 'Hostels & PGs',           Icon: Home,          tagline: 'Tenant verification & check-in' },
  { id: 'retail',     label: 'Retail & Malls',          Icon: ShoppingBag,   tagline: 'Staff, contractor & VIP entry' },
  { id: 'logistics',  label: 'Logistics & Warehouses',  Icon: Truck,         tagline: 'Gate pass & contractor check-in' },
  { id: 'age-gated',  label: 'Age Gated Retail',        Icon: ShoppingBag,   tagline: 'Age verification for regulated products' },
  { id: 'temple',     label: 'Temples & Trusts',        Icon: Building,      tagline: 'Devotee registration & events' },
  { id: 'pilgrim',      label: 'Pilgrim Sites',           Icon: Building,      tagline: 'VIP darshan check-in' },
  { id: 'construction', label: 'Construction Sites',      Icon: HardHat,       tagline: 'Worker attendance & site check-in' },
];

function templatePath(sectorId) {
  if (sectorId === 'corporate') return '/proposals/corporate/GatePass/proposal-corporate-gatepass.html';
  return `/proposals/${sectorId}/proposal-${sectorId}.html`;
}

function fillPlaceholders(html, { name, designation, org, city }) {
  return html
    .replace(/\[RECIPIENT_NAME\]/g, name)
    .replace(/\[DESIGNATION\]/g, designation)
    .replace(/\[ORGANISATION_NAME\]/g, org)
    .replace(/\[CITY\]/g, city)
    .replace(/(<title>IDGate[^<]*?)<\/title>/, `$1 · ${org}</title>`)
    // inject auto-print just before </body>
    .replace('</body>', '<script>window.addEventListener(\'load\',function(){window.print();});</script>\n</body>');
}

const FIELD_CLASSES =
  'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 ' +
  'transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200';

const LABEL_CLASSES = 'mb-1.5 block text-sm font-semibold text-slate-700';

export default function Proposal() {
  const [sector, setSector]   = useState('hotel');
  const [form, setForm]       = useState({ name: '', designation: '', org: '', city: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, designation, org, city } = form;
    if (!name || !designation || !org || !city) {
      setError('Please fill in all fields.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(templatePath(sector));
      if (!res.ok) throw new Error(`Template not found (${res.status})`);
      const raw  = await res.text();
      const html = fillPlaceholders(raw, form);

      // Open personalised HTML in a new tab — browser print dialog → Save as PDF
      const blob = new Blob([html], { type: 'text/html' });
      const url  = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // Revoke after a short delay so the new tab has time to load it
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Could not load template. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedSector = SECTORS.find((s) => s.id === sector) || SECTORS[0];

  return (
    <>
      <SEOHead
        title="Generate Proposal — IDGate"
        description="Generate a personalised IDGate proposal PDF for any sector in seconds."
        keywords="IDGate proposal, Aadhaar check-in proposal, generate proposal PDF"
        url="https://idgate.in/proposal"
      />

      {/* Hero */}
      <div className="border-b border-slate-200 bg-white">
        <div className="container-custom py-14 lg:py-20">
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-accent-600">
            Sales tool
          </p>
          <h1 className="max-w-2xl font-black tracking-tight">
            <span className="block text-3xl font-semibold text-slate-900 lg:text-4xl">
              Generate a personalised
            </span>
            <span className="demo-hero-gradient block text-5xl leading-tight lg:text-7xl">
              proposal PDF.
            </span>
          </h1>
          <p className="mt-4 max-w-xl text-lg text-slate-600">
            Select the sector, fill in the recipient details, and get a
            ready-to-send branded proposal — no server required.
          </p>
        </div>
      </div>

      <div className="bg-slate-50">
        <div className="container-custom py-12 lg:py-16">
          <div className="mx-auto max-w-3xl">

            {/* Step 1 — Sector picker */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="mb-8 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100"
            >
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-400">Step 1</p>
              <h2 className="mb-4 text-lg font-bold text-slate-900">Choose sector</h2>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                {SECTORS.map(({ id, label, Icon, tagline }) => {
                  const active = id === sector;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => { setSector(id); setSuccess(false); }}
                      className={`flex w-full items-center gap-2.5 rounded-xl border px-3.5 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                        active
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <Icon size={16} className={active ? 'shrink-0 text-primary-600' : 'shrink-0 text-slate-400'} />
                      <div>
                        <p className={`text-xs font-semibold leading-tight ${active ? 'text-primary-800' : 'text-slate-800'}`}>
                          {label}
                        </p>
                        <p className={`text-[10px] leading-snug ${active ? 'text-primary-500' : 'text-slate-400'}`}>
                          {tagline}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>

            {/* Step 2 — Recipient details */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100"
            >
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-400">Step 2</p>
              <h2 className="mb-5 text-lg font-bold text-slate-900">Recipient details</h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className={LABEL_CLASSES}>Recipient name</label>
                    <input id="name" name="name" type="text" placeholder="e.g. Rahul Sharma"
                      value={form.name} onChange={handleChange} className={FIELD_CLASSES} />
                  </div>
                  <div>
                    <label htmlFor="designation" className={LABEL_CLASSES}>Designation</label>
                    <input id="designation" name="designation" type="text" placeholder="e.g. General Manager"
                      value={form.designation} onChange={handleChange} className={FIELD_CLASSES} />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="org" className={LABEL_CLASSES}>Organisation</label>
                    <input id="org" name="org" type="text" placeholder="e.g. Taj Hotels"
                      value={form.org} onChange={handleChange} className={FIELD_CLASSES} />
                  </div>
                  <div>
                    <label htmlFor="city" className={LABEL_CLASSES}>City</label>
                    <input id="city" name="city" type="text" placeholder="e.g. Mumbai"
                      value={form.city} onChange={handleChange} className={FIELD_CLASSES} />
                  </div>
                </div>

                {error && (
                  <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>
                )}

                {success && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="rounded-lg bg-green-50 px-4 py-3 text-sm font-medium text-green-700"
                  >
                    ✓ Proposal opened in a new tab — use <strong>File → Print → Save as PDF</strong> to download.
                  </motion.div>
                )}

                <div className="pt-1">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2.5 rounded-xl bg-primary-700 px-6 py-3.5 text-sm font-bold text-white transition hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  >
                    {loading ? (
                      <><Loader2 size={16} className="animate-spin" />Loading…</>
                    ) : (
                      <><FileDown size={16} />Open {selectedSector.label} Proposal</>
                    )}
                  </button>
                  <p className="mt-2.5 text-center text-xs text-slate-400">
                    Opens in a new tab · print dialog auto-launches · save as PDF
                  </p>
                </div>
              </form>
            </motion.div>

          </div>
        </div>
      </div>
    </>
  );
}
